/**
 * HOW A MODULE REACHES A CORE SURFACE WITHOUT CORE NAMING IT.
 *
 * Ported unchanged in behaviour from the POC (`core/lib/registry.ts`). A core
 * surface exposes a registry (patient tabs, report tabs, search sources…); a
 * module registers into it when its root loads. Rules:
 *   - replaced, never mutated: the list is a new array on every change, so
 *     useSyncExternalStore sees a stable snapshot between changes
 *   - idempotent by id: registering the same id twice keeps one, the latest
 *   - ordered registries sort by `order`, absent being 50
 */
import { useSyncExternalStore } from "react";

export interface Contribution {
  id: string;
}

export interface OrderedContribution extends Contribution {
  order?: number;
}

export interface Registry<T extends Contribution> {
  /** Adds or replaces by id. Returns a function that removes exactly this item. */
  register(item: T): () => void;
  list(): readonly T[];
  /** The list, live, inside a component. */
  use(): readonly T[];
  subscribe(listener: () => void): () => void;
  /** For tests. */
  reset(): void;
}

const DEFAULT_ORDER = 50;

export function createRegistry<T extends Contribution>(opts: { ordered?: boolean } = {}): Registry<T> {
  let items: readonly T[] = [];
  const listeners = new Set<() => void>();

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };
  const emit = () => listeners.forEach((fn) => fn());
  const orderOf = (item: T) => (item as OrderedContribution).order ?? DEFAULT_ORDER;
  const list = () => items;

  return {
    register(item) {
      const next = [...items.filter((i) => i.id !== item.id), item];
      items = opts.ordered ? next.sort((a, b) => orderOf(a) - orderOf(b)) : next;
      emit();
      return () => {
        items = items.filter((i) => i !== item);
        emit();
      };
    },
    list,
    use: () => useSyncExternalStore(subscribe, list, list),
    subscribe,
    reset() {
      items = [];
      emit();
    },
  };
}
